import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { formatInCST } from '../utils/timezone';
import {
  Database, Upload, CheckCircle, AlertCircle, Loader2, Link2,
  Users, FileText, Ticket, Package, Clock, ArrowRight,
  RefreshCw, Eye, ChevronDown, ChevronUp, X, Save, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

export default function WHMCSImport() {
  const [credentials, setCredentials] = useState({
    api_url: '',
    auth_method: 'api_credentials',
    api_identifier: '',
    api_secret: '',
    admin_username: '',
    admin_password: '',
    access_key: ''
  });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedSettings, setSavedSettings] = useState(null);
  const [usingSaved, setUsingSaved] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadSavedSettings();
    loadHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadSavedSettings = async () => {
    try {
      const response = await api.get('/import/settings/whmcs');
      if (response.data.saved) {
        setSavedSettings(response.data);
        setCredentials({
          api_url: response.data.credentials.api_url || '',
          auth_method: response.data.credentials.auth_method || 'api_credentials',
          api_identifier: response.data.credentials.api_identifier || '',
          api_secret: '',
          admin_username: response.data.credentials.admin_username || '',
          admin_password: '',
          access_key: '',
        });
        setUsingSaved(true);
      }
    } catch (error) {
      console.error('Failed to load saved settings', error);
    }
  };

  const handleSaveSettings = async () => {
    if (!credentials.api_url) {
      toast.error('Please fill in the WHMCS URL before saving');
      return;
    }

    let credsToSave = { ...credentials };

    // If using saved and secret fields are empty, merge with existing saved values
    if (usingSaved) {
      try {
        const fullCreds = await api.post('/import/settings/whmcs/load');
        const saved = fullCreds.data.credentials;
        if (!credsToSave.api_secret) credsToSave.api_secret = saved.api_secret || '';
        if (!credsToSave.access_key) credsToSave.access_key = saved.access_key || '';
        if (!credsToSave.admin_password) credsToSave.admin_password = saved.admin_password || '';
      } catch (e) { /* ignore */ }
    }

    setSaving(true);
    try {
      await api.post('/import/settings/whmcs', credsToSave);
      toast.success('Connection settings saved');
      await loadSavedSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSettings = async () => {
    try {
      await api.delete('/import/settings/whmcs');
      setSavedSettings(null);
      setUsingSaved(false);
      setCredentials({ api_url: '', auth_method: 'api_credentials', api_identifier: '', api_secret: '', admin_username: '', admin_password: '', access_key: '' });
      toast.success('Saved settings deleted');
    } catch (error) {
      toast.error('Failed to delete settings');
    }
  };

  const getActiveCredentials = async () => {
    const isApiCreds = credentials.auth_method === 'api_credentials';
    const secretEmpty = isApiCreds ? !credentials.api_secret : !credentials.admin_password;

    if (usingSaved && secretEmpty) {
      try {
        const response = await api.post('/import/settings/whmcs/load');
        const saved = response.data.credentials;
        return {
          ...credentials,
          api_secret: credentials.api_secret || saved.api_secret || '',
          admin_password: credentials.admin_password || saved.admin_password || '',
          access_key: credentials.access_key || saved.access_key || '',
        };
      } catch (e) {
        toast.error('Failed to load saved credentials');
        return null;
      }
    }
    return credentials;
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/import/history?source=whmcs&limit=20');
      setImportHistory(response.data);
    } catch (error) {
      console.error('Failed to load import history', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleTestConnection = async () => {
    const creds = await getActiveCredentials();
    if (!creds) return;
    if (!creds.api_url) {
      toast.error('Please fill in the WHMCS URL');
      return;
    }
    const isApiCreds = creds.auth_method === 'api_credentials';
    if (isApiCreds && (!creds.api_identifier || !creds.api_secret)) {
      toast.error('Please fill in the API Identifier and Secret');
      return;
    }
    if (!isApiCreds && (!creds.admin_username || !creds.admin_password)) {
      toast.error('Please fill in the Admin Username and Password');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/import/whmcs/test', creds);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Successfully connected to WHMCS');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Connection test failed';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const startImport = async (dataType) => {
    const creds = await getActiveCredentials();
    if (!creds) return;
    if (!creds.api_url) {
      toast.error('Please enter WHMCS credentials and test the connection first');
      return;
    }
    try {
      const response = await api.post(`/import/whmcs/${dataType}`, creds);
      const jobId = response.data.job_id;
      setActiveJob({ id: jobId, status: 'pending', data_type: dataType, progress_current: 0, progress_total: 0, progress_message: 'Starting...' });
      toast.success(`${dataType} import started`);
      startPolling(jobId);
    } catch (error) {
      toast.error(`Failed to start ${dataType} import`);
    }
  };

  const startPolling = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/import/jobs/${jobId}`);
        const job = response.data;
        setActiveJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          loadHistory();
          if (job.status === 'completed') {
            toast.success('Import completed successfully');
          } else {
            toast.error(`Import failed: ${job.error || 'Unknown error'}`);
          }
        }
      } catch (error) {
        console.error('Polling error', error);
      }
    }, 2000);
  }, []);

  const isRunning = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');

  const importCards = [
    { type: 'customers', label: 'Customers', icon: Users, desc: 'Client accounts and contacts', color: 'text-blue-600' },
    { type: 'invoices', label: 'Invoices', icon: FileText, desc: 'Billing and payment records', color: 'text-emerald-600' },
    { type: 'tickets', label: 'Tickets', icon: Ticket, desc: 'Support tickets and history', color: 'text-amber-600' },
    { type: 'products', label: 'Products', icon: Package, desc: 'Services and product catalog', color: 'text-purple-600' },
  ];

  const getStatusBadge = (status) => {
    const map = {
      completed: <Badge data-testid="status-badge-completed" className="bg-emerald-100 text-emerald-700 border-emerald-200">Completed</Badge>,
      failed: <Badge data-testid="status-badge-failed" className="bg-red-100 text-red-700 border-red-200">Failed</Badge>,
      running: <Badge data-testid="status-badge-running" className="bg-blue-100 text-blue-700 border-blue-200">Running</Badge>,
      pending: <Badge data-testid="status-badge-pending" className="bg-neutral-100 text-neutral-700 border-neutral-200">Pending</Badge>,
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return formatInCST(dateStr, 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getResultSummary = (results) => {
    if (!results) return '-';
    const parts = [];
    for (const [key, val] of Object.entries(results)) {
      if (val && typeof val === 'object') {
        const created = val.created || 0;
        const updated = val.updated || 0;
        parts.push(`${key}: ${created} new, ${updated} updated`);
      }
    }
    return parts.length > 0 ? parts.join(' | ') : '-';
  };

  return (
    <div data-testid="whmcs-import-page" className="space-y-6 p-6">
      {/* Page Header */}
      <div>
        <h1 data-testid="whmcs-page-title" className="text-2xl font-bold text-neutral-900 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50">
            <Database className="w-6 h-6 text-blue-600" />
          </div>
          WHMCS Data Import
        </h1>
        <p className="text-sm text-neutral-500 mt-1 ml-12">
          Import customers, invoices, tickets, and products from your WHMCS installation
        </p>
      </div>

      <Tabs defaultValue="import" className="w-full">
        <TabsList data-testid="whmcs-tabs">
          <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        {/* ===== IMPORT TAB ===== */}
        <TabsContent value="import" className="space-y-6 mt-4">
          {/* Credentials Card */}
          <Card data-testid="whmcs-credentials-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="w-5 h-5 text-neutral-500" />
                Connection Settings
              </CardTitle>
              <CardDescription>Enter your WHMCS API credentials to establish a connection</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auth Method Toggle */}
              <div className="space-y-2">
                <Label>Authentication Method</Label>
                <div className="flex gap-2" data-testid="whmcs-auth-method-toggle">
                  <Button
                    variant={credentials.auth_method === 'api_credentials' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCredentials({ ...credentials, auth_method: 'api_credentials' })}
                    disabled={isRunning}
                    className={credentials.auth_method === 'api_credentials' ? 'bg-[#1a5490] hover:bg-[#0f3a5f]' : ''}
                    data-testid="auth-method-api-credentials"
                  >
                    API Credentials
                  </Button>
                  <Button
                    variant={credentials.auth_method === 'admin_login' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCredentials({ ...credentials, auth_method: 'admin_login' })}
                    disabled={isRunning}
                    className={credentials.auth_method === 'admin_login' ? 'bg-[#1a5490] hover:bg-[#0f3a5f]' : ''}
                    data-testid="auth-method-admin-login"
                  >
                    Admin Login
                  </Button>
                  <span className="text-xs text-neutral-400 self-center ml-2">
                    {credentials.auth_method === 'api_credentials'
                      ? 'Uses Identifier + Secret from WHMCS API Credentials'
                      : 'Uses Admin Username + Password (legacy method)'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="whmcs-url">WHMCS URL</Label>
                  <Input
                    id="whmcs-url"
                    data-testid="whmcs-url-input"
                    value={credentials.api_url}
                    onChange={(e) => setCredentials({ ...credentials, api_url: e.target.value })}
                    placeholder="https://your-domain.com/whmcs"
                    disabled={isRunning}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whmcs-access-key">API Access Key</Label>
                  <Input
                    id="whmcs-access-key"
                    data-testid="whmcs-access-key-input"
                    type="password"
                    value={credentials.access_key}
                    onChange={(e) => { setCredentials({ ...credentials, access_key: e.target.value }); setUsingSaved(false); }}
                    placeholder={savedSettings?.has_values?.access_key ? "Using saved key (leave empty to keep)" : "From configuration.php ($api_access_key)"}
                    disabled={isRunning}
                  />
                  <p className="text-xs text-neutral-400">Required to bypass WHMCS API IP restriction</p>
                </div>

                {credentials.auth_method === 'api_credentials' ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="whmcs-identifier">API Identifier</Label>
                      <Input
                        id="whmcs-identifier"
                        data-testid="whmcs-identifier-input"
                        value={credentials.api_identifier}
                        onChange={(e) => setCredentials({ ...credentials, api_identifier: e.target.value })}
                        placeholder="Your API Identifier"
                        disabled={isRunning}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whmcs-secret">API Secret</Label>
                      <Input
                        id="whmcs-secret"
                        data-testid="whmcs-secret-input"
                        type="password"
                        value={credentials.api_secret}
                        onChange={(e) => { setCredentials({ ...credentials, api_secret: e.target.value }); setUsingSaved(false); }}
                        placeholder={savedSettings?.has_values?.api_secret ? "Using saved secret (leave empty to keep)" : "Your API Secret"}
                        disabled={isRunning}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="whmcs-admin-username">Admin Username</Label>
                      <Input
                        id="whmcs-admin-username"
                        data-testid="whmcs-admin-username-input"
                        value={credentials.admin_username}
                        onChange={(e) => setCredentials({ ...credentials, admin_username: e.target.value })}
                        placeholder="WHMCS Admin Username"
                        disabled={isRunning}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whmcs-admin-password">Admin Password</Label>
                      <Input
                        id="whmcs-admin-password"
                        data-testid="whmcs-admin-password-input"
                        type="password"
                        value={credentials.admin_password}
                        onChange={(e) => { setCredentials({ ...credentials, admin_password: e.target.value }); setUsingSaved(false); }}
                        placeholder={savedSettings?.has_values?.admin_password ? "Using saved password (leave empty to keep)" : "WHMCS Admin Password"}
                        disabled={isRunning}
                      />
                      <p className="text-xs text-neutral-400">Password is MD5-hashed before sending to WHMCS</p>
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  data-testid="whmcs-test-connection-btn"
                  onClick={handleTestConnection}
                  variant="outline"
                  disabled={testing || isRunning}
                  className="min-w-[160px]"
                >
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>

                <Button
                  data-testid="whmcs-save-settings-btn"
                  onClick={handleSaveSettings}
                  variant="outline"
                  disabled={saving || isRunning}
                  className="min-w-[140px]"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>

                {savedSettings && (
                  <Button
                    data-testid="whmcs-delete-settings-btn"
                    onClick={handleDeleteSettings}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={isRunning}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clear Saved
                  </Button>
                )}

                {testResult && (
                  <div data-testid="whmcs-test-result" className="flex items-center gap-2 text-sm">
                    {testResult.success ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        <span className="text-emerald-700">
                          Connected - {testResult.details?.total_clients} clients, {testResult.details?.total_products} products found
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <span className="text-red-700">{testResult.message}</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {savedSettings && (
                <div data-testid="whmcs-saved-indicator" className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Settings saved{savedSettings.updated_at ? ` on ${formatInCST(savedSettings.updated_at, 'MMM d, yyyy h:mm a')}` : ''}</span>
                  <span className="text-neutral-300">|</span>
                  <span>Secret fields are auto-loaded from saved settings when left empty</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Job Progress */}
          {activeJob && (
            <Card data-testid="whmcs-active-job-card" className="border-blue-200 bg-blue-50/30">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {isRunning ? (
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    ) : activeJob.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                    <span className="font-medium text-neutral-800">
                      {activeJob.data_type?.charAt(0).toUpperCase() + activeJob.data_type?.slice(1)} Import
                    </span>
                    {getStatusBadge(activeJob.status)}
                  </div>
                  {!isRunning && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="dismiss-job-btn"
                      onClick={() => setActiveJob(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-sm text-neutral-600 mb-2">{activeJob.progress_message}</p>
                {activeJob.progress_total > 0 && (
                  <div className="space-y-1">
                    <Progress
                      data-testid="whmcs-import-progress"
                      value={activeJob.progress_total > 0 ? (activeJob.progress_current / activeJob.progress_total) * 100 : 0}
                    />
                    <p className="text-xs text-neutral-500 text-right">
                      {activeJob.progress_current} / {activeJob.progress_total}
                    </p>
                  </div>
                )}
                {activeJob.status === 'completed' && activeJob.results && (
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(activeJob.results).map(([key, val]) => (
                      <div key={key} className="bg-white rounded-lg p-3 border border-neutral-200">
                        <p className="text-xs text-neutral-500 capitalize">{key}</p>
                        <p className="text-lg font-semibold text-neutral-800">{val?.created || 0} <span className="text-sm font-normal text-neutral-500">new</span></p>
                        {(val?.updated || 0) > 0 && (
                          <p className="text-xs text-neutral-500">{val.updated} updated</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {activeJob.error && (
                  <Alert className="mt-3 border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-sm">{activeJob.error}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Import Type Cards */}
          <div>
            <h2 className="text-base font-semibold text-neutral-800 mb-3">Import Data</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {importCards.map((card) => (
                <Card
                  key={card.type}
                  data-testid={`whmcs-import-card-${card.type}`}
                  className="hover:shadow-md transition-shadow duration-200"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg bg-neutral-50 ${card.color}`}>
                        <card.icon className="w-5 h-5" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-neutral-800 mb-1">{card.label}</h3>
                    <p className="text-xs text-neutral-500 mb-4">{card.desc}</p>
                    <Button
                      data-testid={`whmcs-import-btn-${card.type}`}
                      onClick={() => startImport(card.type)}
                      disabled={isRunning}
                      variant="outline"
                      className="w-full"
                      size="sm"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      Import {card.label}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-4">
              <Button
                data-testid="whmcs-import-all-btn"
                onClick={() => startImport('all')}
                disabled={isRunning}
                className="bg-[#1a5490] hover:bg-[#0f3a5f] text-white"
              >
                {isRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {isRunning ? 'Import Running...' : 'Import All Data'}
              </Button>
            </div>
          </div>

          {/* How It Works */}
          <Alert className="bg-neutral-50 border-neutral-200">
            <AlertCircle className="h-4 w-4 text-neutral-500" />
            <AlertDescription className="text-neutral-700 text-sm">
              <strong>How it works:</strong> Data is fetched from WHMCS via its API, mapped to CipherOne records,
              and inserted or updated (deduplicating by external ID or email match). Large imports run in the background
              and progress updates in real time.
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* ===== HISTORY TAB ===== */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-neutral-800">Import History</h2>
            <Button
              data-testid="refresh-history-btn"
              variant="outline"
              size="sm"
              onClick={loadHistory}
              disabled={historyLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${historyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {importHistory.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500 font-medium">No import history yet</p>
                <p className="text-sm text-neutral-400 mt-1">Import data from the Import tab to see history here</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table data-testid="whmcs-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Data Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead className="w-[80px]">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importHistory.map((entry) => (
                    <TableRow key={entry.id || entry.job_id}>
                      <TableCell className="text-sm text-neutral-600">{formatDate(entry.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{entry.data_type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell className="text-sm text-neutral-600 max-w-[300px] truncate">
                        {entry.error || getResultSummary(entry.results)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`history-detail-btn-${entry.id}`}
                          onClick={() => setDetailDialog(entry)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="import-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Import Details
              {detailDialog && getStatusBadge(detailDialog.status)}
            </DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500">Source</p>
                  <p className="font-medium text-neutral-800 capitalize">{detailDialog.source}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Data Type</p>
                  <p className="font-medium text-neutral-800 capitalize">{detailDialog.data_type}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Date</p>
                  <p className="font-medium text-neutral-800">{formatDate(detailDialog.created_at)}</p>
                </div>
                <div>
                  <p className="text-neutral-500">Job ID</p>
                  <p className="font-mono text-xs text-neutral-600">{detailDialog.job_id}</p>
                </div>
              </div>

              {detailDialog.error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">{detailDialog.error}</AlertDescription>
                </Alert>
              )}

              {detailDialog.results && Object.keys(detailDialog.results).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">Results</p>
                  <div className="space-y-2">
                    {Object.entries(detailDialog.results).map(([key, val]) => (
                      <div key={key} className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
                        <p className="text-sm font-medium text-neutral-700 capitalize mb-1">{key}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-neutral-500">Created: </span>
                            <span className="font-medium text-emerald-700">{val?.created || 0}</span>
                          </div>
                          <div>
                            <span className="text-neutral-500">Updated: </span>
                            <span className="font-medium text-blue-700">{val?.updated || 0}</span>
                          </div>
                          <div>
                            <span className="text-neutral-500">Skipped: </span>
                            <span className="font-medium text-neutral-600">{val?.skipped || 0}</span>
                          </div>
                        </div>
                        {val?.errors && val.errors.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            <p className="font-medium mb-1">{val.errors.length} errors:</p>
                            {val.errors.slice(0, 5).map((err, i) => (
                              <p key={i} className="truncate">{err}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
